<template>
  <div>
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <h2 class="text-lg font-medium">
        <app-i18n code="notification.title"></app-i18n>
      </h2>
    </div>
    <!-- BEGIN: HTML Table Data -->
    <div class="intro-y box p-5 mt-5">
      <div
        class="flex flex-col sm:flex-row sm:items-end xl:items-start justify-end"
      >
        <div class="flex mt-5 sm:mt-0">
          <button
            v-if="hasPermissionToCreate"
            class="btn bg-theme-36 text-white w-1/2 sm:w-auto mr-2 ml-2"
            @click="$router.push('/notification/new')"
          >
            <PlusIcon class="w-5 h-5 mr-2 ml-2" /> {{ i18n('common.create') }}
          </button>
        </div>
      </div>
      <div class="overflow-x-auto scrollbar-hidden">
        <div ref="table" class="relative">
          <loading-spinner
            type="half-circle"
            :duration="1000"
            :size="40"
            color="#4469A8"
            :loading="loading"
          />
          <div class="overflow-x-auto">
            <table
              class="table table-report sm:mt-2"
              id="tablePrint"
              ref="tableRef"
            >
              <thead>
                <tr>
                  <th class="whitespace-nowrap w-10" id="ignore-6">#</th>
                  <th
                    v-for="col in columns"
                    :key="col.name"
                    :id="col.name === 'action' ? 'ignore-1' : ''"
                    class="text-center whitespace-nowrap w-20"
                  >
                    {{ i18n(col.label) }}
                  </th>
                </tr>
              </thead>
              <tbody v-if="rows.length">
                <tr
                  v-for="(row, index) in rows"
                  :key="index"
                  class="intro-x"
                  :class="{ removedRow: row.isRemoved }"
                >
                  <td class="w-10">
                    <div class="text-gray-600 text-xs whitespace-nowrap mt-0.5">
                      {{ rowIndex(index) }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="font-medium whitespace-nowrap w-full truncate">
                      {{ presenterLocalization(row, 'title') }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="font-medium whitespace-nowrap w-full truncate">
                      {{ presenterLocalization(row, 'body') }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="font-medium whitespace-nowrap">
                      {{ presenter(row, 'userType') }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="font-medium whitespace-nowrap">
                      {{ presenterDate(row, 'createdAt') }}
                    </div>
                  </td>
                  <td class="table-report__action w-56">
                    <div class="flex justify-center items-center">
                      <a
                        data-toggle="modal"
                        data-target="#notification-view-modal-preview"
                        href="javascript:;"
                        class="text-theme-10 dark:text-theme-30 w-20"
                        @click="doOpenModal(row)"
                      >
                        <EyeIcon
                          class="w-4 h-4"
                          :class="isRTL ? 'ml-2' : 'mr-2'"
                        />
                        <app-i18n code="common.view"></app-i18n>
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <app-empty-page
              v-if="!rows.length && !loading"
              :label="i18n('empty.notification')"
              label-class="text-lg font-medium"
              icon-size="4x"
              class="box w-full py-64"
            ></app-empty-page>
          </div>
          <Pagination
            v-if="rows.length"
            :page="currentPage"
            :pageSize="pagination.limit"
            :rowsNumber="pagination.total"
            :showSizeChanger="true"
            :hasNext="hasNext"
            :hasPrev="hasPrev"
            :loading="loading"
            :disabled="!rows.length"
            position="center"
            @next-page="doNextPage"
            @prev-change="doPrevPage"
            @size-change="doChangePaginationPageSize"
            @page-change="goToPage"
          ></Pagination>
        </div>
      </div>
    </div>
    <!-- END: HTML Table Data -->
    <!-- BEGIN: Modal -->
    <NotificationViewModal :record="selectedRow" @close="doCloseModal" />
    <!-- END: Modal -->
  </div>
</template>

<script>
import NotificationViewModal from '@/views/notification/notification-view-modal.vue'
import { mapGetters, mapActions } from 'vuex'
import { NotificationPermissions } from './notification-permissions'
import { paginationMixin } from '@/mixins/pagination-mixin'
import { presenterMixin } from '@/mixins/presenter-mixin'

export default {
  mixins: [paginationMixin, presenterMixin],
  components: {
    NotificationViewModal
  },
  setup() {
    const showModal = () => {
      cash('#notification-view-modal-preview').modal('show')
    }

    const hideModal = () => {
      cash('#notification-view-modal-preview').modal('hide')
    }
    return {
      showModal,
      hideModal
    }
  },
  data() {
    return {
      // data important for Mixins
      fetchOptions: {},
      // component Data
      selectedRow: null,
      visibleModal: false,
      columns: [
        {
          name: 'title',
          field: 'data',
          label: 'notification.fields.title',
          align: 'center'
        },
        {
          name: 'body',
          field: 'data',
          label: 'notification.fields.body',
          align: 'center'
        },
        {
          name: 'type',
          field: 'type',
          label: 'notification.fields.recipients',
          align: 'center'
        },
        {
          name: 'createdAt',
          field: 'createdAt',
          label: 'notification.fields.createdAt',
          align: 'center'
        },
        {
          name: 'action',
          field: 'action',
          label: 'common.actions',
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      isRTL: 'layout/isRTL',
      language: 'layout/currentLanguageCode',
      currentUser: 'auth/currentUser',
      hasNext: 'notification/list/hasNext',
      hasPrev: 'notification/list/hasPrev',
      rows: 'notification/list/rows',
      pagination: 'notification/list/pagination',
      loading: 'notification/list/loading'
    }),
    hasPermissionToCreate() {
      return new NotificationPermissions(this.currentUser).create
    },
    currentPage() {
      return this.pagination.currentPage || 1
    },
    rowIndex() {
      return index => {
        return index + this.pagination.limit * (this.currentPage - 1) + 1
      }
    }
  },
  created() {
    document.title =
      this.i18n('routes.app') + ' | ' + this.i18n('routes.notifications')
  },
  watch: {
    isRTL() {
      document.title =
        this.i18n('routes.app') + ' | ' + this.i18n('routes.notifications')
    }
  },
  async mounted() {
    await this.doFetchFirstPage()
  },
  methods: {
    ...mapActions({
      doFetch: 'notification/list/doFetch',
      doFetchNextPage: 'notification/list/doFetchNextPage',
      doFetchPreviousPage: 'notification/list/doFetchPreviousPage',
      doChangePaginationCurrentPage:
        'notification/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize: 'notification/list/doChangePaginationPageSize'
    }),
    i18n(key, args) {
      return this.$t(key, args)
    },
    doOpenModal(row) {
      this.selectedRow = row
      this.visibleModal = true
    },
    doCloseModal() {
      this.selectedRow = null
      this.visibleModal = false
    }
  }
}
</script>
<style scoped>
.table-report:not(.table-report--bordered):not(.table-report--tabulator) tr {
  border-radius: 0px !important;
  box-shadow: 15px 0px 15px #0000000b;
}
.table-report:not(.table-report--bordered):not(.table-report--tabulator) td {
  border-radius: 0px !important;
  box-shadow: unset !important;
}
.pagination-ar {
  margin-right: unset !important;
  margin-left: auto !important;
}
</style>
